import React, { useState, useContext, useEffect } from "react"

import ChoiceList from "./ChoiceList"
import LinkWithArrow from "../../../lib/Components/LinkWithArrow"
import TradeInThemeContext from "../TradeInWizard/contexts/theme"
import { Choice } from "./carAssemblageTypes"
import { CommonChoiceProps } from "./SingleChoice"
import isEqual from "lodash/isEqual"
import { trackFunction } from "website_script/lib/iframeTrackingHelper"

function discardChoicesCategoryAndFlatten(
  categorizedChoices: { options: Choice[]; category?: string }[]
): Choice[] {
  // @ts-expect-error lib option in tsconfig points to es6, when .flat wasn't a thing
  return categorizedChoices.map(({ options }) => options).flat()
}

function areChoicesEqual(a: Choice[], b: Choice[]) {
  return (
    a.length === b.length &&
    a.every((choice) =>
      b.some((otherChoice) =>
        Object.keys(choice).every((key) => {
          const typedKey = key as keyof Choice

          return typedKey === "value"
            ? isEqual(choice[typedKey], otherChoice[typedKey])
            : choice[typedKey] === otherChoice[typedKey]
        })
      )
    )
  )
}

type ToggleChoiceListProps = {
  otherChip: JSX.Element | null
  possibleCategorizedChoices: { options: Choice[]; category?: string }[]
  allCategorizedChoices: { options: Choice[]; category?: string }[]
  labels: {
    seeAll: string
    close: string
    continue: string
    nestedMultiChoicesPrompt: string
  }
} & CommonChoiceProps

const ToggleChoiceList = ({
  possibleCategorizedChoices,
  allCategorizedChoices,
  nestedMultiChoices,
  fullWidthOptions,
  selectItem,
  labels,
  selected,
  isSellYourCar,
  showConfirmButtonAfterSelection,
  otherChip,
  stepName,
}: ToggleChoiceListProps) => {
  const theme = useContext(TradeInThemeContext)
  const possibleChoices = discardChoicesCategoryAndFlatten(
    possibleCategorizedChoices
  )
  const allChoices = discardChoicesCategoryAndFlatten(allCategorizedChoices)
  const selectedInAllChoices = allChoices.find(
    (choice) => choice.value === selected
  )
  const [expanded, setExpanded] = useState(
    Boolean(selectedInAllChoices) || possibleChoices.length === 0
  )

  const shouldShowToggle = () => {
    if (areChoicesEqual(possibleChoices, allChoices)) return false

    if (possibleChoices.length === 0 || allChoices.length === 0) return false

    return allCategorizedChoices.length > 0
  }

  useEffect(() => {
    if (otherChip) {
      trackFunction({
        entity: "car_form",
        element: `other for ${stepName}`,
        action: "displayed",
        flow: "trade_in",
        type: "micro_proceeding",
      })
    }
  }, [])

  return (
    <>
      {(expanded ? allCategorizedChoices : possibleCategorizedChoices).map(
        (choiceList, i) => (
          <React.Fragment key={i}>
            <ChoiceList
              selectItem={selectItem}
              choices={choiceList.options ?? []}
              nestedMultiChoices={nestedMultiChoices}
              fullWidthOptions={fullWidthOptions}
              category={choiceList.category ?? ""}
              selected={selected}
              labels={labels}
              isSellYourCar={isSellYourCar}
              showConfirmButtonAfterSelection={showConfirmButtonAfterSelection}
              otherChip={otherChip}
            />
          </React.Fragment>
        )
      )}

      {shouldShowToggle() && (
        <LinkWithArrow
          label={expanded ? labels.close : labels.seeAll}
          color={theme.color}
          onClick={() => {
            setExpanded(!expanded)
            trackFunction({
              entity: "car_form",
              action: "clicked",
              element: `toggle all options for ${stepName}`,
              details: !expanded ? "show all" : "show less",
              flow: "trade_in",
              type: "micro_proceeding",
            })
          }}
          direction={expanded ? "up" : "down"}
        />
      )}
    </>
  )
}

export default ToggleChoiceList
